import { useEffect, useState } from 'react'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import { EmailMessage } from 'models/emailIntegration'
import { format } from 'date-fns'
import apiService from 'services/api'
import to from 'await-to-js'
import { Editor } from 'components/Editor/Editor'
import { ChevronLeft, ChevronRight, Paperclip } from 'lucide-react'
import FbButton from 'components/FbUI/FbButton'

interface MobileViewEmailModalProps {
  isOpen: boolean
  onClose: () => void
  emails: EmailMessage[]
  initialIndex: number
  emailType: 'received' | 'sent'
}

export function MobileViewEmailModal({
  isOpen,
  onClose,
  emails,
  initialIndex,
  emailType,
}: MobileViewEmailModalProps) {
  const api = apiService()
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [isLoading, setIsLoading] = useState(true)
  const [emailData, setEmailData] = useState<EmailMessage | null>(null)
  const [recipientsExpanded, setRecipientsExpanded] = useState(false)

  const currentEmail = emails[currentIndex]

  useEffect(() => {
    setCurrentIndex(initialIndex)
  }, [initialIndex])

  useEffect(() => {
    if (isOpen && currentEmail?.id) {
      loadEmailContent()
    }
  }, [isOpen, currentIndex])

  const loadEmailContent = async () => {
    if (!currentEmail?.id) return

    setIsLoading(true)
    const [err, response] = await to(
      api.emailIntegration.getEmailMessageById(currentEmail.id)
    )

    if (err) {
      console.error('Error loading email content', err)
      setIsLoading(false)
      return
    }

    setEmailData(response)
    setIsLoading(false)
  }

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
      setRecipientsExpanded(false)
    }
  }

  const handleNext = () => {
    if (currentIndex < emails.length - 1) {
      setCurrentIndex(currentIndex + 1)
      setRecipientsExpanded(false)
    }
  }

  const formatDate = (date: string) => {
    return format(new Date(date), 'MMM d, yyyy h:mm a')
  }

  // Get sender info
  const getSenderInfo = () => {
    const contacts = currentEmail?.related_contacts || []
    if (contacts.length === 0)
      return { initials: '?', name: 'Unknown', email: '' }

    const first = contacts[0]
    const initials =
      `${first.first_name?.[0] || ''}${first.last_name?.[0] || ''}`.toUpperCase() ||
      '?'
    const name =
      `${first.first_name || ''} ${first.last_name || ''}`.trim() || 'Unknown'
    return { initials, name, email: '' }
  }

  const sender = getSenderInfo()

  if (!isOpen) return null

  return (
    <MobileModal
      handleBack={onClose}
      backLabel="Back"
      header={
        <div className="space-y-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            {emailType === 'received' ? 'Received Emails' : 'Sent Emails'}
          </p>
          <h1 className="text-xl font-semibold text-gray-900 leading-tight">
            {currentEmail?.subject || '(no subject)'}
          </h1>
          <p className="text-sm text-gray-500">
            {currentEmail?.date ? formatDate(currentEmail.date) : ''}
          </p>
        </div>
      }
      footer={
        <div className="flex justify-between items-center gap-4">
          <FbButton
            variant="secondary"
            size="md"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="flex items-center gap-1"
          >
            <ChevronLeft size={16} />
            Prev
          </FbButton>
          <FbButton
            variant="secondary"
            size="md"
            onClick={handleNext}
            disabled={currentIndex >= emails.length - 1}
            className="flex items-center gap-1"
          >
            Next
            <ChevronRight size={16} />
          </FbButton>
        </div>
      }
    >
      {/* Sender info */}
      <div className="flex items-start gap-3 py-3 border-b">
        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
          {sender.initials}
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={() => setRecipientsExpanded(!recipientsExpanded)}
            className="text-left w-full"
          >
            <p className="font-medium text-gray-900">{sender.name}</p>
            <p className="text-sm text-gray-500 truncate">
              to me
              {currentEmail?.related_contacts?.length > 1
                ? `, +${currentEmail.related_contacts.length - 1} more`
                : ''}
              {!recipientsExpanded && <span className="ml-1">▼</span>}
            </p>
          </button>
          {recipientsExpanded && (
            <div className="mt-2 text-sm text-gray-600 space-y-1">
              {currentEmail?.related_contacts?.map((contact, idx) => (
                <p key={idx}>
                  {contact.first_name} {contact.last_name}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Email body */}
      <div className="py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">Loading email content...</p>
          </div>
        ) : (
          <div className="prose prose-sm max-w-none">
            <Editor value={emailData?.body || ''} readOnly disableToolbar />
          </div>
        )}
      </div>

      {/* Attachments */}
      {emailData?.attachments && emailData.attachments.length > 0 && (
        <div className="py-4 border-t">
          {emailData.attachments.map((attachment, index) => (
            <a
              key={index}
              href="#"
              className="flex items-center gap-2 text-green-700 hover:text-green-800 py-1"
              onClick={(e) => {
                e.preventDefault()
                // Handle attachment download
              }}
            >
              <Paperclip size={16} />
              <span className="text-sm underline">{attachment.filename}</span>
            </a>
          ))}
        </div>
      )}
    </MobileModal>
  )
}
